import { Info } from 'lucide-react'
import { useMemo } from 'react'
import { ExportFormat, formatBytes } from '@/core/export'
import { useEditSession } from '@/core/store/editSession'
import { selectExportEstimates } from '@/core/store/selectors'
import { TEST_IDS } from '@/lib/testIds'
import { cn } from '@/lib/utils'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/ui/shadcn/tooltip'
import { useExactQualitySizes } from '@/tools/quality/qualitySizeCache'

export function ExportEstimate({ dimensions, className }: { dimensions?: string; className?: string } = {}) {
  const document = useEditSession((state) => state.document)
  const sources = useEditSession((state) => state.sources)
  const exportOptions = useEditSession((state) => state.exportOptions)
  const estimates = useMemo(
    () => selectExportEstimates({ document, sources, exportOptions }),
    [document, exportOptions, sources],
  )
  const exactSizes = useExactQualitySizes(document, sources, exportOptions)
  const first = estimates.outputs[0]
  if (first === undefined) throw new Error('Export estimate is missing its first output')
  if (!document) throw new Error('Export estimate requires a loaded document')
  const exactBytes = exportOptions.format === ExportFormat.Jpeg ? exactSizes?.jpegBytes : exactSizes?.pngBytes
  const label = exactBytes !== undefined && estimates.outputs.length === 1
    ? `${formatBytes(exactBytes).replace(/^~/, '')} (real)`
    : estimates.outputs.length === 1
      ? formatBytes(first)
    : `${estimates.outputs.length} images · ${formatBytes(first)} each · ${formatBytes(estimates.total)} total`
  const exact = exactBytes !== undefined && estimates.outputs.length === 1

  return (
    <div className={cn('mt-2 flex items-center justify-center gap-1.5 text-xs text-muted-foreground', dimensions && 'mt-0', className)} data-testid={TEST_IDS.exportEstimate}>
      <span className="flex min-w-0 items-center gap-1.5 truncate">
        {dimensions ? <span className="shrink-0 tabular-nums" data-testid={TEST_IDS.outputDimensions}>{dimensions}</span> : null}
        {dimensions ? <span aria-hidden="true">·</span> : null}
        <span className="truncate">{label}</span>
      </span>
      <Tooltip>
        <TooltipTrigger asChild>
          <button type="button" className="rounded-sm outline-offset-2" aria-label="About export size estimate">
            <Info className="size-3.5" aria-hidden="true" />
          </button>
        </TooltipTrigger>
        <TooltipContent side="top">{exact
          ? 'Measured with the same browser encoder used for export.'
          : 'Rough estimate. The real encoded file size may differ.'}</TooltipContent>
      </Tooltip>
    </div>
  )
}
